import React from "react";
import { Link } from "react-router-dom";
import { Home, Map, Sparkles, Gift, Store, Trophy, Tv } from "lucide-react";

/**
 * HeaderNavLinks — the shared link cluster (Home / Map / Commissions /
 * Gifts / Marketplace) used on both desktop nav and the mobile sheet.
 *
 * Props:
 *   user    — current auth user (controls Gifts visibility)
 *   mobile  — when true, renders the 48-px touch-target variant so taps
 *             on iOS don't accidentally hit adjacent rows.
 *   onClose — called after every link tap to dismiss the parent sheet
 *             (no-op on desktop).
 */
const HeaderNavLinks = ({ user, mobile = false, onClose = () => {} }) => {
  // Logged-out users still see Commissions in nav — clicking it pops
  // the auth modal so they discover the feature, then sign up.
  const handleGatedClick = (e) => {
    onClose();
    if (!user) {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent("open-auth"));
    }
  };
  // Home & logo clicks fire a global event so FeedPage can rewind back
  // to the Global playlist. Without this a user stuck on an empty
  // Following view can't escape via Home/logo (React Router doesn't
  // re-mount when navigating to the same route they're already on).
  const handleHomeClick = () => {
    onClose();
    window.dispatchEvent(new CustomEvent("globaleyes:reset-feed-source"));
  };
  // Mobile-specific class adds a real touch target + disables the
  // 300ms tap-delay → fixes the "click registers on the item below"
  // glitch on iOS by guaranteeing each row is at least 48px tall and
  // synthetic clicks resolve immediately on tapend.
  // Desktop: icons only, no labels (per Jun 7 spec — header was too
  // text-heavy and pushed Leaderboard into the logo). Mobile keeps
  // labels because the sheet is a vertical list where icons alone
  // would be unscannable.
  const mobileCls = mobile
    ? "py-3 px-4 hover:bg-gray-800 rounded-lg min-h-[48px]"
    : "p-2 rounded-lg hover:bg-gray-800/60";
  const mobileStyle = mobile ? { touchAction: "manipulation" } : undefined;
  const baseCls = `text-gray-300 hover:text-white transition-colors flex items-center gap-2 ${mobileCls}`;
  // Wrap the label so we can render it only on mobile while keeping
  // a single source of truth for the link copy. NOT defined as a
  // sub-component (would destroy the subtree on every parent render,
  // per React reconciliation rules) — just a plain helper.
  const label = (text) => (mobile ? <span>{text}</span> : null);
  return (
    <>
      <Link to="/" className={baseCls} style={mobileStyle} onClick={handleHomeClick} data-testid="nav-home" title={mobile ? undefined : "Home"} aria-label="Home">
        <Home className="h-4 w-4" /> {label("Home")}
      </Link>
      {/* "My Feed" was collapsed into a Global/Following toggle on the
          Home dynamic screen — the standalone nav item is no longer needed. */}
      <Link to="/map" className={baseCls} style={mobileStyle} onClick={onClose} data-testid="nav-map" title={mobile ? undefined : "Map"} aria-label="Map">
        <Map className="h-4 w-4" /> {label("Map")}
      </Link>
      <Link to="/commissions" className={baseCls} style={mobileStyle} onClick={handleGatedClick} data-testid="nav-commissions" title={mobile ? undefined : "Commissions"} aria-label="Commissions">
        <Sparkles className="h-4 w-4" /> {label("Commissions")}
      </Link>
      <Link to="/marketplace" className={baseCls} style={mobileStyle} onClick={onClose} data-testid="nav-marketplace" title={mobile ? undefined : "Marketplace"} aria-label="Marketplace">
        <Store className="h-4 w-4" /> {label("Marketplace")}
      </Link>
      <Link to="/leaderboard" className={baseCls} style={mobileStyle} onClick={onClose} data-testid="nav-leaderboard" title={mobile ? undefined : "Leaderboard"} aria-label="Leaderboard">
        <Trophy className="h-4 w-4" /> {label("Leaderboard")}
      </Link>
      <Link to="/tv" className={baseCls} style={mobileStyle} onClick={onClose} data-testid="nav-tv" title={mobile ? undefined : "Watch on TV"} aria-label="Watch on TV">
        <Tv className="h-4 w-4" /> {label("Watch on TV")}
      </Link>
      {/* Gifts moved to the END of the row per Jun 7 spec — it's
          the most actionable item (claim something) so it sits at
          the natural rightmost end of the desktop icon strip. */}
      {user && (
        <Link to="/gifts" className={baseCls} style={mobileStyle} onClick={onClose} data-testid="nav-gifts" title={mobile ? undefined : "Gifts"} aria-label="Gifts">
          <Gift className="h-4 w-4" /> {label("Gifts")}
        </Link>
      )}
    </>
  );
};

export default HeaderNavLinks;
